import React from 'react';

export interface OverrideEditButtonProps {
  /** The line item whose override this edits — used for the accessible name. */
  liitem: string;
  onClick: () => void;
  /** Usually the override's reason, so hovering explains what's being edited. */
  title?: string;
  className?: string;
}

/**
 * The way back into a recorded override — reword it, or withdraw it.
 *
 * Shared rather than repeated because it appears on four surfaces (the estimate's
 * overridden rows and its rejected-charges list, the invoicing tab's line-item grid and
 * its callouts) and they must not drift apart: the control opens the same dialog on the
 * same record every time, so it should look identical every time.
 *
 * A button, not a link — it performs an action rather than navigating, and the underline
 * styling it replaced read as a footnote.
 *
 * Deliberately "Edit", not "Override". Override means "override this charge"; offering
 * it on a line already overridden reads as doing it twice.
 */
export const OverrideEditButton: React.FC<OverrideEditButtonProps> = ({
  liitem,
  onClick,
  title,
  className = '',
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={`Edit override on ${liitem}`}
    className={
      'shrink-0 inline-flex items-center rounded border border-slate-300 bg-white px-1.5 ' +
      'py-0.5 text-[0.6875rem] font-medium text-slate-700 hover:bg-slate-100 ' +
      'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 ' +
      className
    }
  >
    Edit
  </button>
);

export default OverrideEditButton;
